import { Trans, useLingui } from "@lingui/react/macro";
import type { Bot, NavLink } from "@rakazo/contracts";
import { accountDisplay } from "@rakazo/core";
import {
  Button,
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  Sidebar,
} from "@rakazo/ui-web";
import { Search, SlidersHorizontal, Tickets, Users } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { PasskeyOffer } from "../../components/PasskeyOffer";
import { SidebarFooter } from "../../components/SidebarFooter";
import { useBilling } from "../../components/SubscriptionGate";
import { formatBillingDate } from "../../lib/billing";
import type { BotsSidebarDensity } from "../../lib/bots-sidebar-density-pref";
import type { Panel } from "../../lib/right-panel-state";
import { clearSpaceSelection } from "../../lib/rpc";
import { useSignOut } from "../../lib/sign-out";
import { ActivityList } from "../ActivityList";
import { SpaceSearchResults } from "../SpaceSearch";
import type { SettingsSection } from "../shell-overlay";
import { SidebarHeader } from "./sidebar-header";
import type { SidebarSearch } from "./use-sidebar-search";

type User = { name?: string | null; email?: string; image?: string | null };

/** The bots sidebar: a drawer on phones, collapsible to icons elsewhere. */
export function ShellSidebar({
  isMobile,
  mobileOpen,
  setMobileOpen,
  density,
  collapsed,
  mini,
  setCollapsedPref,
  createMenuOpen,
  setCreateMenuOpen,
  search,
  bots,
  setPanel,
  navigate,
  setNewSpaceOpen,
  setCreateBotOpen,
  roster,
  archived,
  footerLogos,
  user,
  ticketBoardEnabled,
  serviceView,
  onServiceView,
  navLinks,
  canManageAppSettings = false,
  openSettings,
  openIntegrations,
}: {
  isMobile: boolean;
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
  density: BotsSidebarDensity;
  collapsed: boolean;
  mini: boolean;
  setCollapsedPref: (collapsed: boolean) => void;
  createMenuOpen: boolean;
  setCreateMenuOpen: (open: boolean) => void;
  search: SidebarSearch;
  bots: Bot[];
  setPanel: (panel: Panel) => void;
  navigate: (path: string) => void;
  setNewSpaceOpen: (open: boolean) => void;
  setCreateBotOpen: (open: boolean) => void;
  roster: ReactNode;
  archived: ReactNode;
  footerLogos: Array<{ name: string; logo?: string | null }>;
  user: User | undefined;
  ticketBoardEnabled: boolean;
  serviceView?: "work" | "team" | "tickets";
  onServiceView?: (view: "work" | "team") => void;
  navLinks: NavLink[];
  canManageAppSettings?: boolean;
  openSettings: (section: SettingsSection) => void;
  openIntegrations: () => void;
}) {
  const { t } = useLingui();
  const {
    query,
    setQuery,
    searchOpen,
    setSearchOpen,
    searchHits,
    searchLoading,
    showSpaceSearch,
    jumpToSearchHit,
    activityMode,
    toggleActivityMode,
    activityFilters,
    setActivityFilters,
    activityFiltersExpanded,
    setActivityFiltersExpanded,
    activityFiltersId,
    activityFilterCount,
  } = search;
  return (
    <>
      <button
        type="button"
        aria-label={t`Close navigation`}
        aria-hidden={mobileOpen ? undefined : true}
        inert={mobileOpen ? undefined : true}
        tabIndex={mobileOpen ? undefined : -1}
        onClick={() => setMobileOpen(false)}
        className={`absolute inset-y-0 end-0 start-[min(calc(100%-48px),316px)] z-30 bg-overlay transition-opacity duration-150 ease-out motion-reduce:transition-none md:hidden ${
          mobileOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      />
      <Sidebar
        collapsible={isMobile ? "none" : "icon"}
        side={document.documentElement.dir === "rtl" ? "right" : "left"}
        data-testid="bots-sidebar"
        data-space-switch="sidebar"
        inert={isMobile && !mobileOpen ? true : undefined}
        aria-hidden={isMobile && !mobileOpen ? true : undefined}
        data-bots-sidebar-density={density}
        data-collapsed={collapsed ? "true" : "false"}
        className={
          isMobile
            ? `absolute inset-y-0 start-0 z-40 flex w-[calc(100%-48px)] max-w-[316px] shrink-0 flex-col border-e border-sidebar-border bg-sidebar transition-transform duration-200 ease-out motion-reduce:transition-none ${mobileOpen ? "translate-x-0" : "-translate-x-full rtl:translate-x-full"}`
            : "h-full overflow-hidden border-sidebar-border"
        }
      >
        <SidebarHeader
          mini={mini}
          createMenuOpen={createMenuOpen}
          setCreateMenuOpen={setCreateMenuOpen}
          activityMode={activityMode}
          toggleActivityMode={() => {
            if (mini) setCollapsedPref(false);
            toggleActivityMode();
          }}
          searchOpen={searchOpen && !mini}
          onSearch={
            activityMode && !mini
              ? undefined
              : () => {
                  if (searchOpen && !mini) {
                    setQuery("");
                    setSearchOpen(false);
                    return;
                  }
                  if (mini) setCollapsedPref(false);
                  setSearchOpen(true);
                  requestAnimationFrame(() =>
                    document
                      .querySelector<HTMLInputElement>('input[name="sidebar-search"]')
                      ?.focus(),
                  );
                }
          }
          setBotsSidebarCollapsedPref={setCollapsedPref}
          bots={bots}
          setMobileSidebarOpen={setMobileOpen}
          setPanel={setPanel}
          navigate={navigate}
          setNewSpaceOpen={setNewSpaceOpen}
          setCreateBotOpen={setCreateBotOpen}
        />
        {mini || !(activityMode || searchOpen || query) ? null : (
          <InputGroup
            data-testid="sidebar-search"
            className="mx-2.5 mb-3 w-auto rounded-xl bg-card dark:bg-input border border-border text-muted-foreground focus-within:border-ring"
          >
            <InputGroupAddon>
              <Search size={16} strokeWidth={1.8} aria-hidden="true" />
            </InputGroupAddon>
            <InputGroupInput
              value={activityMode ? activityFilters.query : query}
              onChange={(e) => {
                if (activityMode) setActivityFilters({ ...activityFilters, query: e.target.value });
                else setQuery(e.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key !== "Escape") return;
                if (activityMode) {
                  setActivityFilters({ ...activityFilters, query: "" });
                  return;
                }
                setQuery("");
                setSearchOpen(false);
              }}
              placeholder={activityMode ? t`Search runs` : t`Search`}
              aria-label={activityMode ? t`Search runs` : t`Search`}
              autoComplete="off"
              name="sidebar-search"
            />
            {activityMode ? (
              <InputGroupAddon align="inline-end">
                <InputGroupButton
                  size="icon-sm"
                  aria-label={t`Filters`}
                  aria-expanded={activityFiltersExpanded}
                  aria-controls={activityFiltersId}
                  onClick={() => setActivityFiltersExpanded((expanded) => !expanded)}
                  className="relative rounded-lg"
                >
                  <SlidersHorizontal size={16} aria-hidden="true" />
                  {activityFilterCount > 0 ? (
                    <span
                      data-testid="activity-filter-indicator"
                      aria-hidden="true"
                      className="absolute end-1 top-1 size-1.5 rounded-full bg-primary"
                    />
                  ) : null}
                </InputGroupButton>
              </InputGroupAddon>
            ) : null}
          </InputGroup>
        )}
        <div className="rk-scroll scroll-fade-y scroll-fade-12 flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-2.5 pb-2.5">
          {onServiceView ? (
            <nav aria-label="Customer service" className="mb-2 space-y-1 border-b pb-3">
              {(
                [
                  { id: "work", label: "Tickets", Icon: Tickets },
                  { id: "team", label: "Service team", Icon: Users },
                ] as const
              ).map(({ id, label, Icon }) => (
                <Button
                  key={id}
                  variant="ghost"
                  aria-label={label}
                  aria-current={serviceView === id ? "page" : undefined}
                  className={`min-h-11 w-full rounded-xl ${mini ? "justify-center px-0" : "justify-start gap-3 px-2.5"} ${serviceView === id ? "bg-sidebar-accent" : ""}`}
                  onClick={() => {
                    onServiceView(id);
                    setMobileOpen(false);
                  }}
                >
                  <Icon />
                  {mini ? null : label}
                </Button>
              ))}
            </nav>
          ) : null}
          {showSpaceSearch && !mini ? (
            <SpaceSearchResults
              hits={searchHits}
              loading={searchLoading}
              onSelect={(hit) => jumpToSearchHit(hit)}
            />
          ) : (
            <>
              {activityMode && !mini ? (
                <ActivityList
                  filters={activityFilters}
                  onFiltersChange={setActivityFilters}
                  filtersExpanded={activityFiltersExpanded}
                  filtersId={activityFiltersId}
                  onOpenRun={(run) => {
                    setMobileOpen(false);
                    if (run.groupId) navigate(`/app/g/${run.groupId}`);
                    else navigate(`/app/${run.botId}`);
                  }}
                />
              ) : null}
              {roster}
            </>
          )}
          <div className={mini ? "hidden" : "contents"}>{archived}</div>
        </div>
        <div className={mini ? "hidden" : "contents"}>
          <PasskeyOffer />
        </div>
        <ShellSidebarFooter
          canManageAppSettings={canManageAppSettings}
          mini={mini}
          logos={footerLogos}
          user={user}
          ticketBoardEnabled={ticketBoardEnabled}
          navLinks={navLinks}
          closeMobileSidebar={() => setMobileOpen(false)}
          navigate={navigate}
          openSettings={openSettings}
          openIntegrations={openIntegrations}
        />
      </Sidebar>
    </>
  );
}

function ShellSidebarFooter({
  canManageAppSettings = false,
  mini,
  logos,
  user,
  ticketBoardEnabled,
  navLinks,
  closeMobileSidebar,
  navigate,
  openSettings,
  openIntegrations,
}: {
  mini: boolean;
  logos: Array<{ name: string; logo?: string | null }>;
  user: User | undefined;
  ticketBoardEnabled: boolean;
  navLinks: NavLink[];
  canManageAppSettings?: boolean;
  closeMobileSidebar: () => void;
  navigate: (path: string) => void;
  openSettings: (section: SettingsSection) => void;
  openIntegrations: () => void;
}) {
  const { t, i18n } = useLingui();
  const { status: billing, showPlans } = useBilling();
  const freeUntil =
    billing?.funding === "byok" &&
    billing.byokFreeUntil &&
    new Date(billing.byokFreeUntil) > new Date()
      ? billing.byokFreeUntil
      : null;
  const [menuOpen, setMenuOpen] = useState(false);
  const [logOutError, setLogOutError] = useState<string | null>(null);
  const { signingOut, logOut } = useSignOut(() => {
    clearSpaceSelection();
    navigate("/");
  }, setLogOutError);
  const { label: userName, initial: initials } = accountDisplay(user ?? {}, t`You`);
  const day = freeUntil ? formatBillingDate(freeUntil, i18n.locale || "en") : "";
  return (
    <>
      {canManageAppSettings && freeUntil && !mini ? (
        <div className="mx-2.5 flex items-center justify-between gap-2 ps-2.5 text-sm text-muted-foreground">
          <span className="min-w-0 truncate">
            <Trans>Free plan ends {day}</Trans>
          </span>
          {billing?.canManage ? (
            <Button variant="outline" size="sm" onClick={showPlans}>
              <Trans>Upgrade</Trans>
            </Button>
          ) : null}
        </div>
      ) : null}
      <SidebarFooter
        canManageAppSettings={canManageAppSettings}
        mini={mini}
        logos={logos}
        name={userName}
        initials={initials}
        image={user?.image}
        email={user?.email}
        open={menuOpen}
        onOpenChange={(open) => {
          setMenuOpen(open);
          if (!open) setLogOutError(null);
        }}
        onIntegrations={() => {
          setMenuOpen(false);
          openIntegrations();
        }}
        onBoard={
          ticketBoardEnabled
            ? () => {
                setMenuOpen(false);
                closeMobileSidebar();
                navigate("/app/board");
              }
            : undefined
        }
        links={navLinks}
        onLink={(link) => {
          setMenuOpen(false);
          closeMobileSidebar();
          navigate(`/app/x/${link.id}`);
        }}
        onArtifacts={() => {
          setMenuOpen(false);
          closeMobileSidebar();
          navigate("/app/artifacts");
        }}
        onSettings={() => {
          setMenuOpen(false);
          openSettings("general");
        }}
        onUsage={() => {
          setMenuOpen(false);
          openSettings("usage");
        }}
        onLogOut={() => {
          setLogOutError(null);
          void logOut();
        }}
        signingOut={signingOut}
        logOutError={logOutError}
      />
    </>
  );
}
